import React, { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { getTimelineBefore } from '../api/dataCorrectionApi'
import { formatDuration } from '../model/durationFormat'
import { BEFORE_FAILED, describeOmission, describeWarning } from '../model/messageCatalog'
import { buildMiniRows, markRows, rowIdsOf } from '../model/miniTapes'
import { parseIsoInstant } from '../model/timeGrid'
import MiniTape from './MiniTapes'
import UiMessageAlert from './UiMessageAlert'

const sameTotal = (rows, workerId) => rows.filter((row) => row.workerId === workerId)

// «Job progress» (RN61, A202-4): conditions in this order. No element for the job, a presence anchor or `operation` null = no
// change; a progress that is not measured by time says so; otherwise percentages (`—` when there is no total).
const progressOf = (result, intervention, t_) => {
  if (!intervention.job || !intervention.fabricationOrder) return { text: t_('no change') }
  const derived = result.derived.find((item) => item.fabricationOrderId === intervention.fabricationOrder.id && item.jobReferenceId === intervention.job.id)
  if (!derived || derived.operation === null) return { text: t_('no change') }
  if (derived.progressCalculationType !== 'TIME') return { text: t_('not measured by time') }
  const percent = (side) => (side && side.total ? `${Math.floor((side.current * 100) / side.total)} %` : '—')
  return { before: percent(derived.before), after: percent(derived.after) }
}

// «Imputed time» (RN61): the worker on the job for a job anchor, or the sum of all the worker's jobs for a presence anchor.
const imputedOf = (result, intervention) => {
  const rows = sameTotal(result.impact.imputations, intervention.worker.id)
    .filter((row) => !intervention.job || row.jobReferenceId === intervention.job.id)
  if (rows.length === 0) return undefined
  return {
    beforeMillis: rows.reduce((sum, row) => sum + row.beforeMillis, 0),
    afterMillis: rows.reduce((sum, row) => sum + row.afterMillis, 0)
  }
}

// The «before» tape (RN61, A202-5, A202-9): what is already loaded when it covers `timelineAfter.range`; otherwise one reading of
// the timeline with that range AS SENT BY SUN. If it fails (any non-200, or no answer) only the «after» is drawn, with a note.
const useBeforeBlock = ({ result, deviceId, workerId, loaded }) => {
  const [before, setBefore] = useState({ status: 'loading', block: undefined })
  const range = result.timelineAfter ? result.timelineAfter.range : undefined
  useEffect(() => {
    if (!range) return undefined
    const fromMs = parseIsoInstant(range.from).getTime()
    const toMs = parseIsoInstant(range.to).getTime()
    const covered = loaded.response && loaded.query.from <= fromMs && loaded.query.to >= toMs
      ? loaded.response.timelines.find((block) => block.device.id === deviceId)
      : undefined
    if (covered) {
      setBefore({ status: 'ok', block: covered })
      return undefined
    }
    // A response that arrives when this preview is no longer the one shown is dropped: the component is gone (`alive`).
    let alive = true
    setBefore({ status: 'loading', block: undefined })
    getTimelineBefore(deviceId, workerId, range).then((response) => {
      if (!alive) return
      const block = response && response.timelines ? response.timelines.find((candidate) => candidate.device.id === deviceId) : undefined
      setBefore(block ? { status: 'ok', block } : { status: 'failed', block: undefined })
    }, () => { if (alive) setBefore({ status: 'failed', block: undefined }) })
    return () => { alive = false }
  }, [result]) // eslint-disable-line react-hooks/exhaustive-deps
  return before
}

const Tapes = ({ result, correction, loaded }) => {
  const { t_ } = useNamesTranslation()
  const { intervention, names } = correction
  const deviceId = correction.deviceId
  const before = useBeforeBlock({ result, deviceId, workerId: intervention.worker.id, loaded })
  const range = { fromMs: parseIsoInstant(result.timelineAfter.range.from).getTime(), toMs: parseIsoInstant(result.timelineAfter.range.to).getTime() }
  const afterBlock = result.timelineAfter.timelines.find((block) => block.device.id === deviceId)
  if (!afterBlock || before.status === 'loading') return null
  const anchor = { workerId: intervention.worker.id, jobId: intervention.job ? intervention.job.id : 0 }
  const rowsOf = (block) => buildMiniRows({ block, anchor, range, nowMs: parseIsoInstant(result.timelineAfter.now).getTime(), names, t_ })
  const beforeOk = before.status === 'ok'
  const afterRows = markRows(rowsOf(afterBlock), { changes: result.changes, beforeIds: beforeOk ? rowIdsOf(before.block) : new Set(), compare: beforeOk })
  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
      {beforeOk
        ? <MiniTape title={t_('Before')} testId='mini-before' rows={rowsOf(before.block)} range={range} />
        : <Alert severity='warning' role='status' data-testid='before-failed'>{t_(BEFORE_FAILED.key, BEFORE_FAILED.params)}</Alert>}
      <MiniTape title={t_('After')} testId='mini-after' rows={afterRows} range={range} />
    </Box>
  )
}

const Result = ({ result, correction, loaded }) => {
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  const { intervention, names } = correction
  const imputed = imputedOf(result, intervention)
  const progress = progressOf(result, intervention, t_)
  const remaining = result.impact.remaining
  // Story 211, RN25: the preview of a «close now» (it says so itself) adds what the close does before the trim.
  const closeNow = result.requiresCloseNow === true && result.closeNow ? result.closeNow : undefined
  const closeRows = closeNow ? closeNow.changes.filter((change) => change.role === 'PRIMARY').length : 0
  const pausesForAll = Boolean(closeNow) && correction.draft.tapes.includes('JOB_STATE') && result.warnings.some((warning) => warning.code === 'OTHER_WORKERS_ON_JOB')
  const worker = names.worker(intervention.worker.id)
  const warningContext = {
    format: formatDateTime,
    names,
    workerName: worker,
    jobName: (id) => (intervention.job && intervention.job.id === id ? (intervention.job.name || intervention.job.code) : `#${id}`)
  }
  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {imputed && (
          <Box data-testid='impact-imputed'>
            <Typography variant='caption' color='text.secondary' component='span'>
              {intervention.job
                ? t_('Time imputed to {{worker}} in {{job}}', { worker, job: intervention.job.name || intervention.job.code })
                : t_('Time imputed to {{worker}}', { worker })}
            </Typography>
            <Box component='div' data-down={imputed.afterMillis < imputed.beforeMillis ? 'true' : 'false'} sx={{ fontVariantNumeric: 'tabular-nums', color: imputed.afterMillis < imputed.beforeMillis ? 'success.main' : 'inherit' }}>
              <s>{formatDuration(imputed.beforeMillis, t_)}</s> → <span>{formatDuration(imputed.afterMillis, t_)}</span>
            </Box>
          </Box>
        )}
        <Box data-testid='impact-progress'>
          <Typography variant='caption' color='text.secondary' component='span'>{t_('Job progress')}</Typography>
          <Box component='div'>
            {progress.text ? progress.text : <><s>{progress.before}</s> → <span>{progress.after}</span></>}
          </Box>
        </Box>
        <Box data-testid='impact-rows'>
          <Typography variant='caption' color='text.secondary' component='span'>{t_('Records that change')}</Typography>
          <Box component='div'>
            {result.impact.rowsTouched + closeRows}
            {closeNow && <small data-testid='impact-close-rows'> ({t_('{{count}} of the close', { count: closeRows })})</small>}
          </Box>
        </Box>
        <Box data-testid='impact-remaining'>
          <Typography variant='caption' color='text.secondary' component='span'>{t_('What remains')}</Typography>
          <Box component='div'>{remaining ? `${formatDateTime(parseIsoInstant(remaining.start).getTime())} – ${formatDateTime(parseIsoInstant(remaining.end).getTime())}` : t_('voided')}</Box>
        </Box>
      </Box>
      {closeNow && (
        <Typography variant='body2' data-testid='estimated-close'>
          {t_('Estimated close: {{time}} (orientative)', { time: formatDateTime(parseIsoInstant(closeNow.estimatedAt).getTime()) })}
        </Typography>
      )}
      {result.timelineAfter
        ? <Tapes result={result} correction={correction} loaded={loaded} />
        : result.timelineAfterOmission && <Alert severity='info' role='status' data-testid='omission'>{t_(describeOmission(result.timelineAfterOmission).key)}</Alert>}
      {result.warnings.length > 0 && (
        <Alert severity='warning' data-testid='warnings'>
          <Typography variant='subtitle2' component='div'>{t_('Warnings')} <small>{t_('(they do not prevent applying)')}</small></Typography>
          <Box component='ul' sx={{ m: 0, pl: 2.25 }}>
            {result.warnings.map((warning, index) => {
              const described = describeWarning(warning, warningContext)
              return (
                <li key={`${warning.code}-${index}`}>
                  {t_(described.key, described.params)} <small data-testid='warning-code'>{described.code}</small>
                  {pausesForAll && warning.code === 'OTHER_WORKERS_ON_JOB' && <> <span data-testid='pause-for-all'>{t_('The «close now» would pause the job for all of them.')}</span></>}
                </li>
              )
            })}
          </Box>
        </Alert>
      )}
    </Box>
  )
}

/**
 * What the panel shows of the rehearsal (RN58-RN63): «Calculating…» while it waits or runs (the old preview is hidden), the
 * rejection as a note with its buttons, or the current preview. Nothing before the first change.
 */
const PreviewSummary = ({ correction, loaded, onAction }) => {
  const { t_ } = useNamesTranslation()
  const { preview } = correction
  let body
  if (preview.waiting) {
    body = <Box data-testid='calculating' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={16} /><span>{t_('Calculating the preview…')}</span></Box>
  } else if (preview.phase === 'failed') {
    body = <UiMessageAlert message={preview.failure} onAction={onAction} />
  } else if (preview.vigent) {
    body = <Result result={preview.result} correction={correction} loaded={loaded} />
  }
  if (!body) return null
  return (
    <Box data-testid='preview-section' sx={{ display: 'grid', gap: 1 }}>
      <Typography variant='overline' component='h3' color='text.secondary'>{t_('Correction preview')} <small>{t_('(rehearsal in Sun, nothing is saved)')}</small></Typography>
      {body}
    </Box>
  )
}

export default PreviewSummary
